import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */

export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <h3 {...{
      "id": "boolean-feature-flags"
    }}>{`Boolean feature flags`}</h3>
    <pre><code parentName="pre" {...{
        "className": "language-js-web"
      }}>{`if (posthog.isFeatureEnabled('flag-key') ) {
    // Do something differently for this user

    // Optional: fetch the payload
    const matchedFlagPayload = posthog.getFeatureFlagPayload('flag-key')
}
`}</code></pre>
    <h3 {...{
      "id": "multivariate-feature-flags"
    }}>{`Multivariate feature flags`}</h3>
    <pre><code parentName="pre" {...{
        "className": "language-js-web"
      }}>{`if (posthog.getFeatureFlag('flag-key')  == 'variant-key') { // replace 'variant-key' with the key of your variant
    // Do something differently for this user
    
    // Optional: fetch the payload
    const matchedFlagPayload = posthog.getFeatureFlagPayload('flag-key')
}
`}</code></pre>
    <h3 {...{
      "id": "ensuring-flags-are-loaded-before-usage"
    }}>{`Ensuring flags are loaded before usage`}</h3>
    <p>{`Every time a user loads a page, we send a request in the background to fetch the feature flags that apply to that user. We store those flags in your chosen persistence option (local storage by default).`}</p>
    <p>{`This means that for most pages, the feature flags are available immediately — `}<strong parentName="p">{`except for the first time a user visits`}</strong>{`.`}</p>
    <p>{`To handle this, you can use the `}<inlineCode parentName="p">{`onFeatureFlags`}</inlineCode>{` callback to wait for the feature flag request to finish:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js-web"
      }}>{`posthog.onFeatureFlags(function (flags, flagVariants, { errorsLoading }) {
    // feature flags are guaranteed to be available at this point
    if (posthog.isFeatureEnabled('flag-key')) {
        // do something
    }
})
`}</code></pre>
    <h4 {...{
      "id": "callback-parameters"
    }}>{`Callback parameters`}</h4>
    <p>{`The `}<inlineCode parentName="p">{`onFeatureFlags`}</inlineCode>{` callback receives the following parameters:`}</p>
    <ul>
      <li parentName="ul">
        <p parentName="li"><inlineCode parentName="p">{`flags: string[]`}</inlineCode>{`: An object containing the feature flags that apply to the user.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li"><inlineCode parentName="p">{`flagVariants: Record<string, string | boolean>`}</inlineCode>{`: An object containing the variants that apply to the user.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li"><inlineCode parentName="p">{`{ errorsLoading }: { errorsLoading?: boolean }`}</inlineCode>{`: An object containing a boolean indicating if an error occurred during the request to load the feature flags. This is `}<inlineCode parentName="p">{`true`}</inlineCode>{` if the request timed out or if there was an error. It will be `}<inlineCode parentName="p">{`false`}</inlineCode>{` or `}<inlineCode parentName="p">{`undefined`}</inlineCode>{` if the request was successful.`}</p>
      </li>
    </ul>
    <p>{`You won't usually need to use these, but they are useful if you want to be extra careful about feature flags not being loaded yet because of a network error and/or a network timeout (see `}<inlineCode parentName="p">{`feature_flag_request_timeout_ms`}</inlineCode>{`).`}</p>
    <h3 {...{
      "id": "reloading-feature-flags"
    }}>{`Reloading feature flags`}</h3>
    <p>{`Feature flag values are cached. If something has changed with your user and you'd like to refetch their flag values, call:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js-web"
      }}>{`posthog.reloadFeatureFlags()
`}</code></pre>
    <h3 {...{
      "id": "overriding-server-properties"
    }}>{`Overriding server properties`}</h3>
    <p>{`Sometimes, you might want to evaluate feature flags using properties that haven't been ingested yet, or were set incorrectly earlier. You can do so by setting properties the flag depends on with these calls:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js-web"
      }}>{`posthog.setPersonPropertiesForFlags({'property1': 'value', property2: 'value2'})
`}</code></pre>
    <blockquote>
      <p parentName="blockquote"><strong parentName="p">{`Note:`}</strong>{` These are set for the entire session. Successive calls are additive: all properties you set are combined together and sent for flag evaluation.`}</p>
    </blockquote>
    <p>{`Whenever you set these properties, we also trigger a reload of feature flags to ensure we have the latest values. You can disable this by passing in the optional parameter for reloading:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js-web"
      }}>{`posthog.setPersonPropertiesForFlags({'property1': 'value', property2: 'value2'}, false)
`}</code></pre>
    <p>{`At any point, you can reset these properties by calling `}<inlineCode parentName="p">{`resetPersonPropertiesForFlags`}</inlineCode>{`:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js-web"
      }}>{`posthog.resetPersonPropertiesForFlags()
`}</code></pre>
    <p>{`The same holds for `}<a parentName="p" {...{
        "href": "/manual/group-analytics"
      }}>{`group`}</a>{` properties:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js-web"
      }}>{`// set properties for a group
posthog.setGroupPropertiesForFlags({'company': {'property1': 'value', property2: 'value2'}})

// reset properties for a given group:
posthog.resetGroupPropertiesForFlags('company')

// reset properties for all groups:
posthog.resetGroupPropertiesForFlags()
`}</code></pre>
    <blockquote>
      <p parentName="blockquote"><strong parentName="p">{`Note:`}</strong>{` You don't need to add the group names here, since these properties are automatically attached to the current group (set via `}<inlineCode parentName="p">{`posthog.group()`}</inlineCode>{`). When you change the group, these properties are reset.`}</p>
    </blockquote>
    <h4 {...{
      "id": "automatic-overrides"
    }}>{`Automatic overrides`}</h4>
    <p>{`Whenever you call `}<inlineCode parentName="p">{`posthog.identify`}</inlineCode>{` with person properties, we automatically add these properties to flag evaluation calls to help determine the correct flag values. The same is true for when you call `}<inlineCode parentName="p">{`posthog.group()`}</inlineCode>{`.`}</p>
    <h4 {...{
      "id": "default-overridden-properties"
    }}>{`Default overridden properties`}</h4>
    <p>{`By default, we always override some properties based on the user IP address.`}</p>
    <p>{`The list of properties that this overrides:`}</p>
    <ol>
      <li parentName="ol"><inlineCode parentName="li">{`$geoip_city_name`}</inlineCode></li>
      <li parentName="ol"><inlineCode parentName="li">{`$geoip_country_name`}</inlineCode></li>
      <li parentName="ol"><inlineCode parentName="li">{`$geoip_country_code`}</inlineCode></li>
      <li parentName="ol"><inlineCode parentName="li">{`$geoip_continent_name`}</inlineCode></li>
      <li parentName="ol"><inlineCode parentName="li">{`$geoip_continent_code`}</inlineCode></li>
      <li parentName="ol"><inlineCode parentName="li">{`$geoip_postal_code`}</inlineCode></li>
      <li parentName="ol"><inlineCode parentName="li">{`$geoip_time_zone`}</inlineCode></li>
    </ol>
    <p>{`This enables any geolocation-based flags to work without manually setting these properties.`}</p>
    <h3 {...{
      "id": "request-timeout"
    }}>{`Request timeout`}</h3>
    <p>{`You can configure the `}<inlineCode parentName="p">{`feature_flag_request_timeout_ms`}</inlineCode>{` parameter when initializing your PostHog client to set a flag request timeout. This helps prevent your code from being blocked in the case when PostHog's servers are too slow to respond. By default, this is set at 3 seconds.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`posthog.init('<ph_project_api_key>', { 
  api_host: '<ph_client_api_host>',
  defaults: '<ph_posthog_js_defaults>'
  feature_flag_request_timeout_ms: 3000 // Time in milliseconds. Default is 3000 (3 seconds).
})
`}</code></pre>
    <h3 {...{
      "id": "feature-flag-error-handling"
    }}>{`Feature flag error handling`}</h3>
    <p>{`When using the PostHog SDK, it's important to handle potential errors that may occur during feature flag operations. Here's an example of how to wrap PostHog SDK methods in an error handler:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`function handleFeatureFlag(client, flagKey, distinctId) {
  try {
    const isEnabled = client.isFeatureEnabled(flagKey, distinctId);
    console.log(\`Feature flag '\${flagKey}' for user '\${distinctId}' is \${isEnabled ? 'enabled' : 'disabled'}\`);
    return isEnabled;
  } catch (error) {
    console.error(\`Error fetching feature flag '\${flagKey}': \${error.message}\`);
    // Optionally, you can return a default value or throw the error
    // return false; // Default to disabled
    throw error;
  }
}

// Usage example
try {
  const flagEnabled = handleFeatureFlag(client, 'new-feature', 'user-123');
  if (flagEnabled) {  
    // Implement new feature logic
  } else {
    // Implement old feature logic
  }
} catch (error) {
  // Handle the error at a higher level
  console.error('Feature flag check failed, using default behavior');
  // Implement fallback logic
}
`}</code></pre>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      